import { useCallback, useEffect, useMemo, useState } from 'react'
import { Activity, AlertTriangle } from 'lucide-react'
import { apiGet } from '../lib/api'
import {
  DataTable,
  EmptyState,
  Input,
  PageHeader,
  Pagination,
  StatusBadge,
  TableToolbar,
  THead,
  Th,
  Td,
  Tr,
} from '../components/ui'

interface AuditEvent {
  id: string
  actor: string
  actor_email: string
  action: string
  result: 'allow' | 'deny' | 'error'
  resource_id: string
  resource_name?: string
  source_ip: string
  created_at: string
  details: Record<string, unknown>
}

// Grouped is one or more near-identical AuditEvents (same actor/action/
// resource/result within DEDUPE_WINDOW_MS) collapsed into a single row, so a
// single page load's five forward_auth.decide calls don't read as five
// separate access events.
interface Grouped extends AuditEvent {
  count: number
  firstAt: string
}

const PAGE_SIZE = 20

// Rows for the same actor+action+resource+result within this window are
// treated as one logical event (e.g. every asset request on a page load
// re-triggering forward_auth.decide), not five separate audit entries.
const DEDUPE_WINDOW_MS = 5000

// Human-readable labels for the internal action/event names actually stored
// in audit_events -- kept as a display-layer mapping so the stored values
// stay stable, greppable machine identifiers. Unknown actions fall back to
// the raw string, so a new action type is never hidden, just unprettified
// until this map is updated.
const ACTION_LABELS: Record<string, string> = {
  'dial.allow': 'Connection allowed',
  'dial.deny': 'Connection denied',
  'forward_auth.decide': 'Web request authorized',
  'policy_flip': 'Access revoked mid-session',
  'ssh_network.add': 'SSH network added',
  'ssh_network.remove': 'SSH network removed',
  'cli_settings.update': 'CLI settings updated',
  'policy.create': 'Policy created',
  'policy.update': 'Policy updated',
  'policy.delete': 'Policy deleted',
}

function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action
}

export default function AuditLog() {
  const [events, setEvents] = useState<AuditEvent[]>([])
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [sortAsc, setSortAsc] = useState(false)
  // G31: null = still loading; false = loaded fine (list may legitimately
  // be empty); true = the fetch itself failed. Kept distinct from `events`
  // being empty, which previously looked identical whether the tenant had
  // no history yet or the API call 401/500'd.
  const [loadError, setLoadError] = useState(false)
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    setLoading(true)
    setLoadError(false)
    apiGet<{ events?: AuditEvent[] }>('/api/v1/audit')
      .then((data) => {
        setEvents(Array.isArray(data?.events) ? data.events : [])
      })
      .catch(() => {
        setLoadError(true)
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const searched = useMemo(() => {
    const q = search.toLowerCase()
    return events.filter(
      (e) =>
        (e.actor_email || e.actor || '').toLowerCase().includes(q) ||
        e.action.toLowerCase().includes(q) ||
        actionLabel(e.action).toLowerCase().includes(q) ||
        (e.resource_id || '').toLowerCase().includes(q) ||
        (e.resource_name || '').toLowerCase().includes(q) ||
        e.result.toLowerCase().includes(q),
    )
  }, [events, search])

  // Collapse consecutive same actor+action+resource+result rows within
  // DEDUPE_WINDOW_MS into one row with a count -- grouping runs on true
  // chronological order regardless of the display sort direction, since
  // "near-identical in time" only means something in one direction.
  const grouped = useMemo(() => {
    const chronological = [...searched].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    )
    const groups: Grouped[] = []
    for (const e of chronological) {
      const last = groups[groups.length - 1]
      const withinWindow =
        last &&
        last.actor === e.actor &&
        last.action === e.action &&
        last.resource_id === e.resource_id &&
        last.result === e.result &&
        new Date(e.created_at).getTime() - new Date(last.created_at).getTime() <= DEDUPE_WINDOW_MS
      if (withinWindow) {
        // Keep the group's displayed id/timestamp as the latest event in it;
        // firstAt anchors the "first seen" side of the collapsed range.
        last.created_at = e.created_at
        last.id = e.id
        last.count += 1
      } else {
        groups.push({ ...e, count: 1, firstAt: e.created_at })
      }
    }
    return groups.sort((a, b) => {
      const ta = new Date(a.created_at).getTime()
      const tb = new Date(b.created_at).getTime()
      return sortAsc ? ta - tb : tb - ta
    })
  }, [searched, sortAsc])

  const pageItems = grouped.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <div>
      <PageHeader
        title="Audit Log"
        description="Policy and access events for your tenant"
      />

      <TableToolbar>
        <Input
          type="search"
          placeholder="Search actor, action, resource…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          className="max-w-sm"
        />
      </TableToolbar>

      {loadError ? (
        <EmptyState
          icon={<AlertTriangle className="h-8 w-8" />}
          title="Couldn't load audit events"
          description="The audit log request failed — this may be a temporary issue."
          actionLabel="Retry"
          onAction={load}
        />
      ) : grouped.length === 0 ? (
        <EmptyState
          icon={<Activity className="h-8 w-8" />}
          title={loading ? 'Loading…' : 'No audit events yet'}
        />
      ) : (
        <>
          <DataTable>
            <THead>
              <Tr>
                <Th sortable onSort={() => setSortAsc((s) => !s)}>Time</Th>
                <Th>Actor</Th>
                <Th>Action</Th>
                <Th>Result</Th>
                <Th>Resource</Th>
                <Th>Source IP</Th>
              </Tr>
            </THead>
            <tbody>
              {pageItems.map((e) => (
                <Tr key={e.id}>
                  <Td className="whitespace-nowrap text-muted">
                    {new Date(e.created_at).toLocaleString()}
                    {e.count > 1 && (
                      <span className="ml-1.5 text-xs text-muted">
                        (×{e.count} since {new Date(e.firstAt).toLocaleTimeString()})
                      </span>
                    )}
                  </Td>
                  <Td>{e.actor_email || e.actor}</Td>
                  <Td>
                    <span title={e.action}>{actionLabel(e.action)}</span>
                  </Td>
                  <Td>
                    <StatusBadge
                      status={
                        e.result === 'allow' ? 'success' : e.result === 'deny' ? 'failed' : 'warning'
                      }
                    />
                  </Td>
                  <Td className="text-xs text-muted">
                    {e.resource_name || (
                      <span className="font-mono">{e.resource_id || '—'}</span>
                    )}
                  </Td>
                  <Td className="font-mono text-xs text-muted">{e.source_ip || '—'}</Td>
                </Tr>
              ))}
            </tbody>
          </DataTable>
          <Pagination
            className="mt-4"
            page={page}
            pageSize={PAGE_SIZE}
            total={grouped.length}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  )
}
